import { Routes, UrlMatcher } from '@angular/router';
import { AuthMShellComponent } from './auth-m-shell.component';
import { authGuard } from './core/auth.guard';
import { invitedAuthGuard } from './core/invited-auth.guard';
import { endUserAuthGuard } from './core/end-user-auth.guard';

/**
 * The three Auth-M portals (admin, invited/partner, consumer/patient), each an
 * independent session. Each portal is a non-empty parent path wrapping a shared
 * `.auth-m` shell (so the scoped styles apply and the empty `/` home is never
 * captured here — it falls through to the CMS front-end route).
 *
 * The paths (/admin/*, /invited/*, /end-users/*) match the links Auth M emails
 * (set-password / reset / activate / onboarding); the user-initiated admin
 * pages (login / forgot-password / portal) live under /admin too, so they
 * don't collide with the CMS home routes.
 */
/**
 * Any two-letter address (/ab, /CD, /xY) — exactly one segment of two letters
 * — is the invited (partner) sign-in, alongside /invited/login. Nothing longer
 * or shorter matches, so /x, /invited/…, /end-users/… and the CMS pages are
 * untouched; only a CMS page whose address is two letters would be hidden.
 */
export const twoLetterInvitedLogin: UrlMatcher = (segments) =>
  segments.length === 1 && /^[a-z]{2}$/i.test(segments[0].path) ? { consumed: segments } : null;

export const AUTH_M_ROUTES: Routes = [
  // ── admin portal ────────────────────────────────────────────────────────
  {
    path: 'x',
    component: AuthMShellComponent,
    children: [
      {
        path: '',
        canActivate: [authGuard],
        loadChildren: () => import('./../../components/maw-template/maw-template.routes').then(m => m.MainTemplateRoutes)
      },
      {
        path: 'login',
        loadComponent: () => import('./pages/admin/login.component').then((m) => m.LoginComponent),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./pages/admin/forgot-password.component').then((m) => m.ForgotPasswordComponent),
      },
      {
        path: 'verify-creation',
        loadComponent: () =>
          import('./pages/admin/verify-creation.component').then((m) => m.VerifyCreationComponent),
      },
      {
        path: 'set-password',
        loadComponent: () =>
          import('./pages/admin/set-password.component').then((m) => m.SetPasswordComponent),
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./pages/admin/reset-password.component').then((m) => m.ResetPasswordComponent),
      },
      {
        path: 'reset-pattern',
        loadComponent: () =>
          import('./pages/admin/reset-pattern.component').then((m) => m.ResetPatternComponent),
      },
      {
        // The dashboard is now a fit-style shell (top links → header sections
        // → sidebar subsections) whose navigation comes from PATH-core already
        // filtered for the signed-in user. Former tabs are child routes.
        path: 'dashboard',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/admin/dashboard/dashboard-shell.component').then(
            (m) => m.DashboardShellComponent,
          ),
        children: [
          { path: '', redirectTo: 'users', pathMatch: 'full' },
          {
            path: 'users',
            loadComponent: () =>
              import('./pages/admin/dashboard/admin-users/admin-users.component').then(
                (m) => m.AdminUsersComponent,
              ),
          },
          {
            // Create admin — inside the dashboard shell so the sidebar and
            // section header stay visible. MUST precede users/:id, or ":id"
            // would capture "new".
            path: 'users/new',
            loadComponent: () =>
              import('./pages/admin/create-user.component').then((m) => m.CreateUserComponent),
          },
          {
            // User DETAIL page (Details + Privileges tabs), opened from the
            // Admin users list.
            path: 'users/:id',
            loadComponent: () =>
              import('./pages/admin/dashboard/user-detail/user-detail.component').then(
                (m) => m.UserDetailComponent,
              ),
          },
          {
            path: 'roles',
            loadComponent: () =>
              import('./pages/admin/dashboard/user-roles/user-roles.component').then(
                (m) => m.UserRolesComponent,
              ),
          },
          {
            // Invite user — dedicated page inside the shell (before invited so
            // both resolve as distinct child routes).
            path: 'invited/new',
            loadComponent: () =>
              import('./pages/admin/invite-user.component').then((m) => m.InviteUserComponent),
          },
          {
            path: 'invited',
            loadComponent: () =>
              import('./pages/admin/dashboard/invited-users/invited-users.component').then(
                (m) => m.InvitedUsersComponent,
              ),
          },
          {
            path: 'security',
            loadComponent: () =>
              import('./pages/admin/dashboard/security/security.component').then(
                (m) => m.AdminSecurityComponent,
              ),
          },
          {
            // Sections → E-Atlas (placeholder until the E-Atlas admin screen
            // is ported; the nav item comes from PATH-core).
            path: 'sections/e-atlas',
            loadComponent: () =>
              import('./pages/admin/dashboard/e-atlas/e-atlas.component').then(
                (m) => m.EAtlasComponent,
              ),
          },
          {
            // CMS section — the maw-cms recreation in shared/maw-cms, backed
            // by CMS-service through the gateway.
            path: 'cms',
            loadChildren: () =>
              import('../maw-cms/maw-cms.routes').then((m) => m.MAW_CMS_ROUTES),
          },
        ],
      },
    ],
  },

  // ── invited (partner) portal ──────────────────────────────────────────────
  {
    path: 'invited',
    component: AuthMShellComponent,
    children: [
      {
        path: '',
        canActivate: [invitedAuthGuard],
        loadChildren: () => import('./../../components/front-end/invited-dashboard/invited-dashboard.route').then(m => m.InvitedDashboardRoutes)
      },
      {
        path: 'login',
        loadComponent: () =>
          import('./pages/invited/invited-login.component').then((m) => m.InvitedLoginComponent),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./pages/invited/invited-forgot-password.component').then(
            (m) => m.InvitedForgotPasswordComponent,
          ),
      },
      {
        path: 'set-password',
        loadComponent: () =>
          import('./pages/invited/invited-set-password.component').then(
            (m) => m.InvitedSetPasswordComponent,
          ),
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./pages/invited/invited-reset-password.component').then(
            (m) => m.InvitedResetPasswordComponent,
          ),
      },
      {
        path: 'activate',
        loadComponent: () =>
          import('./pages/invited/invited-activate.component').then(
            (m) => m.InvitedActivateComponent,
          ),
      },
      {
        path: 'reset-pattern',
        loadComponent: () =>
          import('./pages/invited/invited-reset-pattern.component').then(
            (m) => m.InvitedResetPatternComponent,
          ),
      },
      {
        path: 'home',
        canActivate: [invitedAuthGuard],
        loadComponent: () =>
          import('./pages/invited/invited-home.component').then((m) => m.InvitedHomeComponent),
      },
    ],
  },

  // ── invited sign-in at any two-letter address (see twoLetterInvitedLogin) ──
  {
    matcher: twoLetterInvitedLogin,
    component: AuthMShellComponent,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/invited/invited-login.component').then((m) => m.InvitedLoginComponent),
      },
    ],
  },

  // ── consumer (patient) portal ─────────────────────────────────────────────
  {
    path: 'end-users',
    component: AuthMShellComponent,
    children: [
      {
        path: 'register',
        loadComponent: () =>
          import('./pages/consumer/end-user-register/end-user-register.component').then(
            (m) => m.EndUserRegisterComponent,
          ),
      },
      {
        path: 'onboarding',
        loadComponent: () =>
          import('./pages/consumer/end-user-onboarding/end-user-onboarding.component').then(
            (m) => m.EndUserOnboardingComponent,
          ),
      },
      {
        path: 'login',
        loadComponent: () =>
          import('./pages/consumer/end-user-login/end-user-login.component').then((m) => m.EndUserLoginComponent),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./pages/consumer/end-user-forgot-password/end-user-forgot-password.component').then(
            (m) => m.EndUserForgotPasswordComponent,
          ),
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./pages/consumer/end-user-reset-password/end-user-reset-password.component').then(
            (m) => m.EndUserResetPasswordComponent,
          ),
      },
      {
        path: 'reset-pattern',
        loadComponent: () =>
          import('./pages/consumer/end-user-reset-pattern/end-user-reset-pattern.component').then(
            (m) => m.EndUserResetPatternComponent,
          ),
      },
      {
        path: 'activate',
        loadComponent: () =>
          import('./pages/consumer/end-user-activate/end-user-activate.component').then(
            (m) => m.EndUserActivateComponent,
          ),
      },
      {
        path: 'social/callback',
        loadComponent: () =>
          import('./pages/consumer/end-user-social-callback/end-user-social-callback.component').then(
            (m) => m.EndUserSocialCallbackComponent,
          ),
      },
      {
        path: 'home',
        canActivate: [endUserAuthGuard],
        loadComponent: () =>
          import('./pages/consumer/end-user-home/end-user-home.component').then((m) => m.EndUserHomeComponent),
      },
    ],
  },
];
